import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { errorMessage } from '../lib/format';

interface LocationState {
  from?: { pathname: string };
}

type Step = 'email' | 'password';

export function LoginPage(): JSX.Element {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Already signed in → skip the form.
  if (status === 'authenticated') {
    const dest = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={dest} replace />;
  }

  const onContinue = (e: FormEvent): void => {
    e.preventDefault();
    setStep('password');
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      const dest = (location.state as LocationState | null)?.from?.pathname ?? '/';
      navigate(dest, { replace: true });
    } catch (err) {
      setError(errorMessage(err, 'Unable to sign in. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-page dark:bg-zinc-950 px-6 py-12">
      {/* Soft brand glow behind the card — quiet depth cue instead of a flat page. */}
      <div className="absolute inset-0 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none opacity-[0.05] dark:opacity-[0.07]" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
        className="relative z-10 w-full max-w-sm flex flex-col items-center"
      >
        <img
          src="/logo.png"
          alt="Blissful Intelligence"
          className="w-24 h-24 rounded-3xl shadow-soft object-cover mb-8"
        />

        <div className="w-full text-center mb-8">
          <span className="block text-xs font-semibold tracking-[0.2em] uppercase text-brand-600 dark:text-brand-400 mb-2">
            Coach Portal
          </span>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-zinc-900 dark:text-white mb-3 tracking-tight">
            Welcome back
          </h1>
          <p className="text-zinc-500 dark:text-zinc-400 text-sm">
            Sign in to the coach workspace.
          </p>
        </div>

        {error !== null && (
          <div className="w-full mb-6">
            <Alert tone="danger">{error}</Alert>
          </div>
        )}

        <AnimatePresence mode="wait" initial={false}>
          {step === 'email' ? (
            <motion.form
              key="email-step"
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
              className="w-full flex flex-col gap-5"
              onSubmit={onContinue}
              noValidate
            >
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="username"
                placeholder="you@sanathgamage.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              <div className="pt-2">
                <Button type="submit" variant="accent" size="lg" block disabled={email === ''}>
                  Continue
                </Button>
              </div>
            </motion.form>
          ) : (
            <motion.form
              key="password-step"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 12 }}
              transition={{ duration: 0.2 }}
              className="w-full flex flex-col gap-5"
              onSubmit={(e) => void onSubmit(e)}
              noValidate
            >
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setError(null);
                }}
                className="flex items-center gap-2 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors self-start"
              >
                <ArrowLeft className="w-4 h-4" />
                {email}
              </button>
              <Input
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
              <div className="pt-2">
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  block
                  loading={submitting}
                  disabled={password === ''}
                >
                  Sign in
                </Button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        <p className="w-full mt-8 text-xs text-center text-zinc-400 dark:text-zinc-500 font-medium">
          Protected workspace. Access is restricted to coaching staff.
        </p>
      </motion.div>

      <span className="absolute bottom-6 inset-x-0 text-center text-sm text-zinc-400 dark:text-zinc-600 font-medium tracking-wide z-10">
        © Sanath Gamage Coaching
      </span>
    </div>
  );
}
